/**
 * useTestDataStatus Hook
 * Manages App Bridge readiness and test data status fetching
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 3.1
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { TestDataStatus } from '~/types/test-data';

// App Bridge readiness configuration
const MAX_WAIT_MS = 3000;
const POLL_INTERVAL_MS = 50;

/**
 * Get authentication token from Shopify App Bridge
 * Returns null if not in embedded app context or App Bridge not ready
 */
export async function getAuthToken(): Promise<string | null> {
  if (typeof window !== 'undefined' && window.shopify?.idToken) {
    try {
      return await window.shopify.idToken();
    } catch {
      return null;
    }
  }
  return null;
}

interface UseTestDataStatusReturn {
  /** Whether App Bridge is ready for API calls */
  isReady: boolean;
  /** Current test data status */
  status: TestDataStatus | null;
  /** Set status directly (for external updates) */
  setStatus: (status: TestDataStatus | null) => void;
  /** Fetch current test data status */
  fetchStatus: () => Promise<TestDataStatus | null>;
}

/**
 * Hook for managing App Bridge readiness and test data status
 */
export function useTestDataStatus(): UseTestDataStatusReturn {
  const [isReady, setIsReady] = useState(false);
  const [status, setStatus] = useState<TestDataStatus | null>(null);

  const attemptsRef = useRef(0);
  const maxAttempts = MAX_WAIT_MS / POLL_INTERVAL_MS;

  // Poll for App Bridge readiness before allowing API calls
  useEffect(() => {
    let isMounted = true;

    const checkAppBridge = async (): Promise<boolean> => {
      if (typeof window === 'undefined') {
        return false;
      }

      const shopify = window.shopify;
      if (!shopify?.idToken) {
        return false;
      }

      try {
        const token = await shopify.idToken();
        if (token && isMounted) {
          setIsReady(true);
          return true;
        }
      } catch {
        // idToken() throws when not in proper Shopify context
      }
      return false;
    };

    // Attempt check immediately
    checkAppBridge();

    // Poll for App Bridge availability
    const interval = setInterval(async () => {
      attemptsRef.current++;

      if (attemptsRef.current >= maxAttempts) {
        clearInterval(interval);
        return;
      }

      if (await checkAppBridge()) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [maxAttempts]);

  // Fetch test data status (requires App Bridge to be ready)
  const fetchStatus = useCallback(async (): Promise<TestDataStatus | null> => {
    if (!isReady) {
      return null;
    }

    try {
      const token = await getAuthToken();
      if (!token) {
        return null;
      }

      const response = await fetch('/api/test-data/status', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch test data status');
      }
      const data = await response.json() as TestDataStatus;
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  }, [isReady]);

  // Auto-fetch status when App Bridge becomes ready
  useEffect(() => {
    if (isReady) {
      fetchStatus();
    }
  }, [isReady, fetchStatus]);

  return {
    isReady,
    status,
    setStatus,
    fetchStatus,
  };
}
